import React, { useCallback, useState } from 'react';
import { Menu, MenuItem } from '@mui/material';

import {
  TableRow,
  TableCell,
  Checkbox,
  IconButton,
  Divider,
  MoreVertIcon,
} from '@occc/ui-design-system';
import { RetailerNetworkLocationDataFragment } from '@occc/graphql';

type MenuItem = 'addLocationToGroup' | 'removeLocation' | 'locationDetails';

type LocationListItemProps = {
  location: RetailerNetworkLocationDataFragment;
  isItemSelected: boolean;
  handleItemSelect: (id: string) => void;
  handleOpenAddLocationsToLocationGroupDialog: () => void;
  onRemoveLocationMenuItemClick: (locationUuid: string) => void;
  handleOpenLocationDetailsPage: (locationUuid: string) => void;
};

const LocationListItem = ({
  location,
  isItemSelected,
  handleItemSelect,
  handleOpenAddLocationsToLocationGroupDialog,
  onRemoveLocationMenuItemClick,
  handleOpenLocationDetailsPage,
}: LocationListItemProps) => {
  const [actionsForUuid, setActionsForUuid] = useState<string | null>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const getHandlerForAdditionalActions =
    (location: RetailerNetworkLocationDataFragment) => (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();

      setAnchorEl(event.currentTarget);
      setActionsForUuid(location.uuid);
    };

  const handleClose = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
    setActionsForUuid(null);
  }, []);

  const onCheckboxClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    handleItemSelect(location.uuid);
  };

  const onMenuNavigationClick = (itemName: MenuItem) => (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    if (itemName === 'locationDetails') {
      handleOpenLocationDetailsPage(location.uuid);
    }

    if (itemName === 'addLocationToGroup') {
      handleItemSelect(location.uuid);
      handleOpenAddLocationsToLocationGroupDialog();
    }

    if (itemName === 'removeLocation') {
      onRemoveLocationMenuItemClick(location.uuid);
    }

    handleClose(event);
  };

  const siteSystemConfiguration =
    location.siteSystemConfiguration.length > 0 ? location.siteSystemConfiguration[0] : null;

  const locationCity = location?.address?.city || '';
  const locationState = location?.address?.state || '';
  const locationStreet = location?.address?.street1 || '';
  const locationZip = location?.address?.zipCode || '';

  return (
    <TableRow
      key={location.uuid}
      selected={isItemSelected}
      onClick={() => handleOpenLocationDetailsPage(location.uuid)}
    >
      <TableCell padding='checkbox'>
        <Checkbox checked={isItemSelected} onClick={onCheckboxClick} />
      </TableCell>
      <TableCell>{siteSystemConfiguration?.identifier}</TableCell>
      <TableCell>{location.name}</TableCell>
      <TableCell>
        {locationStreet}
        <br />
        {`${locationCity}, ${locationState} ${locationZip}`}
      </TableCell>
      <TableCell>{location?.phoneNumbers?.[0]?.phoneNumber}</TableCell>
      <TableCell align='right'>
        <IconButton onClick={getHandlerForAdditionalActions(location)} sx={{ p: 0 }}>
          <MoreVertIcon />
        </IconButton>
        <Menu
          open={location.uuid === actionsForUuid}
          onClose={handleClose}
          anchorEl={anchorEl}
          PaperProps={{
            sx: {
              borderRadius: '4px',
            },
          }}
        >
          <MenuItem onClick={onMenuNavigationClick('locationDetails')}>
            View Location Details
          </MenuItem>
          <MenuItem onClick={onMenuNavigationClick('addLocationToGroup')}>
            Add to Location Group
          </MenuItem>
          <Divider />
          <MenuItem onClick={onMenuNavigationClick('removeLocation')}>
            Remove from Retailer Network
          </MenuItem>
        </Menu>
      </TableCell>
    </TableRow>
  );
};

export default LocationListItem;
